<template>
  <div>
    <div
      v-if="!orderRoom.attemptedToJoin
        || order.isLoading
        || (orderRoom.joining && !orderRoom.lostConnection)"
      class="cf-flex cf-flex--column cf-flex--align-center cf-flex--justify-center cf-section-loader-container"
    >
      <div class="cf-loader" />
    </div>

    <div
      v-else-if="!isPending2FA"
      class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-16"
    >
      <h1 class="ada-text">
        Checkout
      </h1>

      <section
        v-if="orderDetails.orderType === 'table' && orderRoom.status === 'open'"
        class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
      >
        <div class="container max-width-mobile">
          <template v-if="canAddToOrder">
            <router-link
              class="cf-button cf-button--primary cf-button--lg cf-button--fill"
              :to="{
                name: 'orderAtTableMenu',
                params: {
                  merchantId: $route.params.merchantId
                },
                query: {
                  ml: session.route.merchantLocationId,
                  tb: tableNumber
                }
              }"
            >
              {{ content.PayAtTable.metadata.buttonAddToOrder }}
            </router-link>

            <p class="cf-title cf-title--6 cf-title--uppercase cf-text-align-center">
              or
            </p>
          </template>

          <p class="cf-title cf-title--3 cf-text-align-center">
            {{ content.PayAtTable.metadata.subtitle }}
          </p>
        </div>
      </section>

      <user-list v-if="merchant.options.maxGuestCountForSplitting > 1 && !orderRoom.disableSplit" />

      <cf-validated-form
        name="payment"
        :disabled="orderRoomErrors.hasErrors"
        :extra-validations="extraValidations"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <div
            v-if="orderRoom.orderIsSplit"
            class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
          >
            <div class="container max-width-mobile">
              <div class="toggle-buttons">
                <button
                  class="toggle-buttons__button"
                  :class="{
                    'toggle-buttons__button--loading': orderRoom.changingToSplit === 'even',
                    'toggle-buttons__button--selected': orderRoom.splitType === 'even'
                  }"
                  type="button"
                  @click="changeToEvenSplit"
                >
                  Even Split
                </button>

                <button
                  v-if="!order.discount && !order.externalPaymentAmount && order.orderItems.length > 0"
                  class="toggle-buttons__button"
                  :class="{
                    'toggle-buttons__button--loading': orderRoom.changingToSplit === 'itemized',
                    'toggle-buttons__button--selected': orderRoom.splitType === 'itemized'
                  }"
                  type="button"
                  @click="changeToItemizedSplit"
                >
                  Itemized Split
                </button>

                <button
                  class="toggle-buttons__button"
                  :class="{
                    'toggle-buttons__button--loading': orderRoom.changingToSplit === 'custom',
                    'toggle-buttons__button--selected': orderRoom.splitType === 'custom'
                  }"
                  type="button"
                  @click="changeToCustomSplit"
                >
                  Custom
                </button>
              </div>

              <p class="cf-title cf-title--4 cf-title--heavy cf-title--primary cf-text-align-center">
                <template v-if="orderRoom.splitType === 'even'">
                  Your bill will be evenly split {{ orderRoom.otherUsers.length + 1 }} ways.
                </template>

                <template v-else-if="orderRoom.splitType === 'itemized'">
                  Select the items you ordered.
                </template>

                <template v-else-if="orderRoom.splitType === 'custom'">
                  Pay a specific amount.
                </template>
              </p>

              <div
                v-if="orderRoom.splitType === 'custom' && groupRemainingTotal === 0"
                class="message message--small left animation-grow-in"
              >
                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                  <span class="cf-icon cf-icon--small fa-check success" />
                  <p class="cf-subtitle cf-subtitle--2">
                    Your group balance matches the order total. You can now finish paying.
                  </p>
                </div>
              </div>

              <div
                v-else-if="orderRoom.splitType === 'custom' && groupRemainingTotal < 0"
                class="message message--small left animation-grow-in"
              >
                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                  <span class="cf-icon cf-icon--small fa-exclamation danger" />
                  <p class="cf-subtitle cf-subtitle--2">
                    Your group balance exceeds the <span class="no-wrap">total by {{ dollars(groupRemainingTotal * -1) }}.</span>
                  </p>
                </div>
              </div>

              <div
                v-else
                class="message message--small left"
              >
                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                  <span class="cf-icon far fa-users dark-gray" />
                  <p class="cf-subtitle cf-subtitle--2">
                    Add guests who wish to split the bill before paying. The bill will close after everyone pays.
                  </p>
                </div>
              </div>

              <template v-if="orderRoom.splitType === 'custom'">
                <div class="input-with-content">
                  <div class="input-with-content__input">
                    <cf-text-input
                      v-model="customAmount"
                      type="dollars"
                      label="Amount"
                      name="customAmount"
                      :allow-negative="false"
                      @keypress.enter.prevent="updateYourCustomAmount"
                    />
                  </div>

                  <div class="input-with-content__content">
                    <div class="input-with-content__content-section">
                      <p class="cf-subtitle cf-subtitle--2 cf-text-align-center">
                        <span class="cf-text-size--sm no-wrap">Order Total</span>
                        <br>
                        <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">{{ dollars(order.total) }}</span>
                      </p>
                    </div>

                    <div
                      v-if="groupRemainingTotal === 0"
                      class="input-with-content__content-section"
                    >
                      <p class="cf-subtitle cf-subtitle--2 cf-text-align-center">
                        <span class="cf-text-size--sm">Remaining</span>
                        <br>
                        <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">{{ dollars(groupRemainingTotal) }}</span>
                      </p>
                    </div>

                    <button
                      v-else
                      class="input-with-content__content-section"
                      type="button"
                      @click="updateYourCustomAmountWithRemainingBalance"
                    >
                      <p
                        v-if="groupRemainingTotal > 0"
                        class="cf-subtitle cf-subtitle--2 cf-text-align-center no-wrap-ellipsis"
                      >
                        <span class="cf-text-size--sm cf-text-color--primary">Remaining</span>
                        <br>
                        <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-subtitle--primary">+{{ dollars(groupRemainingTotal) }}</span>
                      </p>

                      <p
                        v-else-if="groupRemainingTotal < 0"
                        class="cf-subtitle cf-subtitle--2 cf-text-align-center no-wrap-ellipsis"
                      >
                        <span class="cf-text-size--sm cf-text-color--danger">Remaining</span>
                        <br>
                        <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-subtitle--danger">{{ dollars(groupRemainingTotal) }}</span>
                      </p>
                    </button>
                  </div>
                </div>

                <button
                  class="cf-button cf-button--primary cf-button--lg cf-button--fill"
                  type="button"
                  :disabled="customAmount === null && orderRoom.you.total === 0 || customAmount === orderRoom.you.total"
                  @click="updateYourCustomAmount"
                >
                  Update Your Amount
                </button>
              </template>
            </div>
          </div>

          <div
            v-if="!orderRoom.orderIsSplit || orderRoom.splitType !== 'custom'"
            class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
          >
            <div class="container max-width-mobile cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16">
              <p class="total">
                <span class="total__title">
                  <span class="total__title-text">
                    {{ content.PayAtTable.metadata.totalTitle }}
                  </span>
                </span>

                <span class="total__amount">
                  {{ dollars(totalWithTip) }}
                </span>
              </p>
            </div>
          </div>

          <div
            v-if="showCreateAccountButton"
            class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
          >
            <button
              type="button"
              class="cf-button cf-button--primary cf-button--lg cf-button--auto-width"
              @click="$openModal('createAccount')"
            >
              Create Account
            </button>
          </div>

          <loyalty-partial-sign-in />

          <div>
            <div class="container max-width-mobile cf-flex cf-flex--column cf-flex--gap-16">
              <a
                v-if="session.route.cancelUrl"
                class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between cf-flex--gap-16"
                :href="session.route.cancelUrl"
              >
                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                  <span
                    class="cf-icon far fa-arrow-left dark-gray"
                    aria-hidden="true"
                  />
                  <h2 class="cf-title cf-title--2 cf-title--uppercase cf-title--primary">
                    <template v-if="merchant.options.flybuyEnabled">
                      Pay Later
                    </template>
                    <template v-else>
                      Go Back
                    </template>
                  </h2>
                </div>

                <p class="cf-title cf-title--6 cf-title--uppercase cf-title--regular">
                  #{{ order.ticketNumber }}
                </p>
              </a>

              <div
                v-else-if="orderRoom.status === 'open'
                  && orderDetails.orderType === 'pay'
                  && session.route.channelTypeId !== 7
                  && session.route.channelTypeId !== 8"
                class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between cf-flex--gap-16"
                role="button"
                @click="goBackToSelectOrder"
              >
                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                  <span
                    class="cf-icon far fa-arrow-left dark-gray"
                    aria-hidden="true"
                  />
                  <h2 class="cf-title cf-title--2 cf-title--uppercase cf-title--primary">
                    Wrong Check #{{ order.ticketNumber }}?
                  </h2>
                </div>

                <p
                  v-if="tableNumberDisplay"
                  class="cf-title cf-title--6 cf-title--uppercase cf-title--regular"
                >
                  {{ userLocationType.description }}
                  {{ tableNumberDisplay }}
                </p>
              </div>

              <div
                v-else
                class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between"
              >
                <h3 class="cf-title cf-title--3 cf-title--uppercase cf-title--primary">
                  {{ content.PayAtTable.metadata.checkNumber }} #{{ order.ticketNumber }}
                </h3>

                <p
                  v-if="tableNumberDisplay"
                  class="cf-title cf-title--6 cf-title--regular cf-title--uppercase"
                >
                  {{ userLocationType.description }}
                  {{ tableNumberDisplay }}
                </p>
              </div>

              <template v-if="order.orderLevelInstructions">
                <hr class="no-spacing-top">
                <h3 class="cf-title cf-title--3">{{ content.PayAtTable.metadata.orderNoteTitle }}</h3>
                <p class="cf-subtitle cf-subtitle--2 cf-text-pre-line">{{ order.orderLevelInstructions }}</p>
              </template>

              <order-items-room
                :existing-payment-amount="closeTabFailure ? tabHoldAmount : 0"
                :your-totals="{
                  serviceCharges: orderRoom.you.serviceCharges,
                  orderFees: orderRoom.you.orderFees,
                  orderFeeTotal: orderRoom.you.orderFeeTotal,
                  tax: orderRoom.you.tax,
                  tip: tip,
                  total: orderRoom.you.total
                }"
              >
                <div class="cf-flex cf-flex--column cf-flex--gap-12 cf-alt-background spacing">
                  <tip-selection
                    v-model="tip"
                    class="cf-flex cf-flex--column cf-flex--gap-12"
                    :label="content.PayAtTable.metadata.tippingTitle"
                    :total="totalForTipPercentage"
                  />

                  <template v-if="orderRoom.status === 'open' && (isPrimaryOrderRoomUser || order.orderAttributes.canAssignAccount)">
                    <!-- Redeemable Checkout Offers -->
                    <cf-cart-offers />

                    <!-- Apply Promo Code -->
                    <cf-apply-discount-code />
                  </template>
                </div>
              </order-items-room>
            </div>
          </div>

          <div
            v-if="order.attachmentUrl"
            class="cf-flex cf-flex--column cf-flex--gap-16"
          >
            <div class="container max-width-mobile">
              <h3 class="cf-title cf-title--3">Attachment</h3>

              <a
                class="card-button card-button--extra-padding no-spacing-children"
                target="_blank"
                :href="order.attachmentUrl"
                @click="orderAttachmentClick"
              >
                <div class="card-button__section grow">
                  <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10">
                    <span class="cf-icon fa-file primary" />
                    <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-subtitle--primary">View Attachment</p>
                  </div>
                </div>
                <div class="card-button__section card-button__section--icon">
                  <span class="cf-icon far fa-external-link gray" />
                </div>
              </a>
            </div>
          </div>

          <div
            v-if="isOpenTabOrder && order.payments[0]"
            class="cf-flex cf-flex--column cf-flex--gap-16"
          >
            <div class="container max-width-mobile">
              <p class="cf-title cf-title--6">Payment Details</p>
              <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-8">
                <div
                  v-if="order.payments[0].paymentType === 'RoomCharge'"
                  class="cf-flex cf-flex--row cf-flex--gap-8"
                >
                  <i class="cf-icon fa-door-closed" />
                  <span class="cf-subtitle cf-subtitle--2">Room Charge</span>
                </div>
                <template v-else>
                  <card-type-icon
                    :card-type="order.payments[0].cardType"
                    :payment-type="order.payments[0].paymentType"
                  />
                  <span class="cf-subtitle cf-subtitle--2">•••• {{ order.payments[0].cardNumberLastFour }}</span>
                </template>
              </div>
            </div>
          </div>

          <div
            v-show="
              !orderRoomErrors.hasErrors
                && (order.subtotal > 0 || order.orderItems.length > 0)
                && order.posOrderStatus !== 'Locked'
                && order.posOrderStatus !== 'PosDegraded'"
            class="cf-flex cf-flex--column cf-flex--gap-16"
          >
            <div class="container max-width-mobile">
              <cf-checkbox-input
                v-model="termsAccepted"
                label="Terms of Use"
                name="termsAccepted"
                class="inline-block"
                :validations="{
                  required: true
                }"
              >
                I accept the

                <button
                  class="cf-link cf-link--brand-secondary cf-link--bold"
                  type="button"
                  @click="$openModal('termsAndConditions')"
                >
                  Terms of Use
                </button>

                <template v-if="terms.privacyPolicy">
                  and

                  <button
                    class="cf-link cf-link--brand-secondary cf-link--bold"
                    type="button"
                    @click="$openModal('privacyPolicy')"
                  >
                    Privacy Policy
                  </button>
                </template>
              </cf-checkbox-input>

              <cf-signature-block
                v-if="signatureRequired && termsAccepted"
                :terms-accepted="termsAccepted"
                class="cf-margin-bottom-10"
                @signature-complete="handleSignatureComplete"
              />

              <template v-if="isOpenTabOrder && order.payments.length">
                <cf-payment-methods
                  v-if="closeTabFailure"
                  :payment-modal-title="content.OpenTabAlternatePaymentMethod.title"
                  :pay-cta-text="content.OpenTabAlternatePaymentMethod.metadata.cta"
                  :primary-cta-text="'Add Payment'"
                  payment-info-open
                  :hidden-elements="{
                    contactInfo: true,
                    giftCards: true,
                    roomCharge: true,
                    signIn: true,
                    smsReceipt: true,
                    tendr: true
                  }"
                  :total="remainingBalance"
                  :disabled="!termsAccepted"
                  :checkout-handler="addAdditionalPayment"
                >
                  <template #payment-modal-top>
                    <div class="cf-flex cf-flex--column cf-flex--gap-12">
                      <p class="cf-subtitle cf-subtitle--2">{{ content.OpenTabAlternatePaymentMethod.metadata.subtitle }}</p>
                    </div>
                  </template>
                </cf-payment-methods>

                <button
                  v-else
                  class="cf-button cf-button--primary cf-button--lg cf-button--fill"
                  :class="{ 'cf-button--loading': isLoading }"
                  type="button"
                  :disabled="!termsAccepted || (signatureRequired && !signatureComplete)"
                  @click="closeTab"
                >
                  {{ content.OpenTabCheckout.metadata.cta }}
                </button>
              </template>

              <cf-payment-methods
                v-else
                :primary-cta-text="content.PayAtTable.metadata.buttonPaymentCta"
                :hidden-elements="{
                  contactInfo: true,
                  total: selectedLocation.features.HidePrices
                }"
                :total="totalWithTip"
                :disabled="!termsAccepted || (signatureRequired && !signatureComplete)"
                reconfirm-order-total
                :checkout-handler="checkout"
              />
            </div>
          </div>
        </div>
      </cf-validated-form>

      <order-room-errors
        v-if="orderRoomErrors.hasErrors"
        class="cf-flex cf-flex--column cf-flex--gap-16"
      />

      <div
        v-else-if="order.posOrderStatus === 'Locked'"
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <div class="container max-width-mobile">
          <p class="message message--danger">
            The order is unavailable for payment. Please notify waitstaff for assistance or wait for this message to clear and retry. [LockedOnPos]
          </p>
        </div>
      </div>

      <div
        v-else-if="order.posOrderStatus === 'PosDegraded'"
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <div class="container max-width-mobile">
          <p class="message message--danger">
            Cannot communicate with the point of sale system. Please pay your waitstaff directly.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import dollars from '@/filters/dollars';
  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';
  import http from '@/services/http';
  import logger from '@/services/logger';
  import pay from '@/services/pay';
  import { mapGetters } from 'vuex';

  import CfApplyDiscountCode from '@/view/components/globals/cf-apply-discount-code/index.vue';
  import CfCartOffers from '@/view/components/globals/cf-cart-offers/index.vue';
  import CfCheckboxInput from '@/view/components/shared/cf-checkbox-input/index.vue';
  import CfPaymentMethods from '@/view/components/globals/cf-payment-methods/index.vue';
  import CfSignatureBlock from '@/view/components/shared/cf-signature-block/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';

  export default {
    name: 'PayAtTableCheckoutPage',

    components: {
      CfApplyDiscountCode,
      CfCartOffers,
      CfCheckboxInput,
      CfPaymentMethods,
      CfSignatureBlock,
      CfTextInput,
      CfValidatedForm
    },

    metaInfo() {
      return {
        title: 'Checkout'
      };
    },

    beforeRouteLeave(to, from, next) {
      const toMerchantRoute = to.matched?.[0]?.name;
      if (!['payAtTableReceipt', 'orderAtTableMenu'].includes(to.name) && toMerchantRoute !== 'merchant') {
        this.leaveOrderRoom();
      }

      next();
    },

    data() {
      return {
        allItemsSelected: false,
        customAmount: null,
        termsAccepted: true,
        tip: 0,
        isLoading: false,
        isPending2FA: false,
        closeTabFailure: false,
        signatureComplete: false,
        signatureData: null
      };
    },

    computed: {
      ...mapGetters('content', ['content', 'terms']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'tableNumber', 'tableNumberDisplay', 'userLocationType']),
      ...mapGetters('session', ['session', 'currentUser', 'orderDetails']),
      ...mapGetters('socket', ['isPrimaryOrderRoomUser', 'orderRoom', 'orderRoomErrors']),

      isOpenTabOrder() {
        return this.order.features?.includes('OpenTab');
      },

      signatureRequired() {
        return this.order.features?.includes('RequireInvoiceSignature');
      },

      groupRemainingTotal() {
        const orderTotalCents = Math.round(this.order.total * 100) - Math.round(this.order.externalPaymentAmount * 100);
        let totalPaidCents = Math.round(this.orderRoom.you.total * 100);

        this.orderRoom.otherUsers.forEach((user) => {
          if (!user.disconnected || user.lockedIn) {
            totalPaidCents += Math.round(user.total * 100);
          }
        });

        return (orderTotalCents - totalPaidCents) / 100;
      },

      totalForTipPercentage() {
        let total = this.order.orderItemTotal || this.order.subtotal;

        if (this.merchant.options.calculateTipAfterTax) {
          total += this.order.tax;
        }

        if (this.order.total === 0) {
          return total;
        }

        if (this.orderRoom.you.total === 0) {
          return 0;
        }

        const percentageOfOrderTotalPaidByUser = this.orderRoom.you.total / this.order.total;
        const totalForTipPercentage = total * percentageOfOrderTotalPaidByUser;

        return Math.round(totalForTipPercentage * 100) / 100;
      },

      totalWithTip() {
        return Math.round((this.tip + this.orderRoom.you.total) * 100) / 100;
      },

      remainingBalance() {
        return Math.round((this.totalWithTip - this.tabHoldAmount) * 100) / 100;
      },

      tabHoldAmount() {
        return this.selectedLocation.defaultTabAmount;
      },

      canAddToOrder() {
        const isRoutedFromTableTopDevice = this.$route.query.ttd;

        return !isRoutedFromTableTopDevice;
      },

      isUserSignedIn() {
        return this.currentUser.accountId;
      },

      isAccountManagementEnabled() {
        return this.merchant.options.signUpEnabled;
      },

      showCreateAccountButton() {
        return this.isAccountManagementEnabled && !this.isUserSignedIn;
      }
    },

    watch: {
      'orderRoom.joining': 'handleUserJoinedRoom',
      'orderRoom.you.total': 'yourTotalChangeHandler'
    },

    created() {
      this.createdHandler();
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      dollars,

      changeToCustomSplit() {
        this.$store.dispatch('socket/changeSplitType', {
          splitType: 'custom'
        });
      },

      changeToEvenSplit() {
        this.$store.dispatch('socket/changeSplitType', {
          splitType: 'even'
        });
      },

      changeToItemizedSplit() {
        this.$store.dispatch('socket/changeSplitType', {
          splitType: 'itemized'
        });
      },

      async checkout(options) {
        try {
          // If signature required, upload it first
          if (this.signatureRequired) {
            await this.uploadSignature();
          }

          const yourPayment = await pay.addPayment({
            contactInfo: options.contactInfo,
            paymentMethod: {
              ...options.paymentMethod,
              ...(this.orderRoom.you.total === 0 && this.tip === 0) && { type: 'cash' }
            },
            order: this.order,
            tip: this.tip
          });

          options.onCheckoutSuccess();

          this.displayReceipt(yourPayment.receiptToken);
        }
        catch (error) {
          errorHandlers.silent(error);

          options.onCheckoutError();
        }
      },

      async closeTab() {
        try {
          this.isLoading = true;

          // If signature required, upload it first
          if (this.signatureRequired) {
            await this.uploadSignature();
          }

          const [payment] = await pay.editAndCompletePayments({
            order: this.order,
            payments: [
              { // the one "hold" payment that already exists for the open tab
                paymentId: this.order.payments[0].id,
                tip: this.tip,
                tax: this.order.tax,
                total: this.totalWithTip
              }
            ]
          });

          this.displayReceipt(payment.receiptToken);
        }
        catch (error) {
          this.closeTabFailure = true;
        }
        finally {
          this.isLoading = false;
        }
      },

      async addAdditionalPayment({ contactInfo, paymentMethod }) {
        /**
         * We're assuming that only one other payment has
         * been made at this point, and that this additional payment
         * will cover the remaining balance not covered by the first.
         * We use the `receiptToken` from the first payment to assign
         * to the second payment so that both payments will be
         * returned when fetching the receipt.
         */
        const { receiptToken } = this.order.payments[0];

        try {
          const payment = await pay.addPayment({
            contactInfo,
            paymentMethod,
            order: this.order,
            receiptToken,
            tip: this.tip,
            total: Math.round((this.remainingBalance - this.tip) * 100) / 100
          });

          this.displayReceipt(payment.receiptToken);
        }

        catch (error) {
          errorHandlers.silent(error);
        }
      },

      createdHandler() {
        this.loadCurrentOrder();

        events.on('order-items:all-items-selected', this.updateAllItemsSelected);
      },

      destroyedHandler() {
        events.off('order-items:all-items-selected', this.updateAllItemsSelected);
      },

      displayReceipt(receiptToken) {
        this.$router.replace({
          name: 'payAtTableReceipt',
          params: {
            merchantId: this.$route.params.merchantId,
            receiptToken
          }
        });
      },

      async extraValidations() {
        let isValid = true;

        if (!this.allItemsSelected) {
          isValid = false;

          events.emit('notification', {
            type: 'error',
            message: 'All items must be selected by guests before paying.'
          });
        }
        else if (this.orderRoom.splitType === 'itemized'
          && this.orderRoom.you.items !== undefined
          && this.orderRoom.you.items.length === 0) {
          isValid = false;

          events.emit('notification', {
            type: 'error',
            message: 'You must select at least one item before paying.'
          });
        }
        else if (this.groupRemainingTotal !== 0) {
          isValid = false;

          if (this.groupRemainingTotal < 0) {
            events.emit('notification', {
              type: 'error',
              message: `Your group balance exceeds the total by ${dollars(this.groupRemainingTotal * -1)}`
            });
          }

          else {
            events.emit('notification', {
              type: 'error',
              message: `A remaining group balance of ${dollars(this.groupRemainingTotal)} still needs to be covered.`
            });
          }
        }
        else if (this.signatureRequired && !this.signatureComplete) {
          isValid = false;

          events.emit('notification', {
            type: 'error',
            message: 'You must complete the required signature before proceeding with payment.'
          });
        }

        if (!isValid) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }

        return isValid;
      },

      goBackToSelectOrder() {
        if (this.session.route.ticketNumber || this.session.route.customQuery) {
          this.$router.replace({
            name: 'makeAPayment',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              ml: this.session.route.merchantLocationId,
              pl: this.session.route.posLocationId
            }
          });
        }

        else {
          this.$router.replace({
            name: 'payAtTableSelectOrder',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              forceDisplay: true
            }
          });
        }
      },

      leaveOrderRoom() {
        this.$store.dispatch('socket/leaveOrderRoom');
      },

      async loadCurrentOrder() {
        try {
          await this.$store.dispatch('orders/getOrderAtTable');

          if (this.isOpenTabOrder) {
            /**
            * add the `ds` (disable split) query param
            * when checking out for an Open Tab order
            * because split checks are not supported
            */
            await this.$router.replace({ query: { ds: 1, ...this.$route.query } })
              .catch(() => {}); /* bury expected vue router error for redundant navigation */
          }

          this.$store.dispatch('socket/joinOrderRoomConditionally');
        }

        catch (error) {
          switch (error.code) {
            case 'MULTIPLE_ORDERS_FOUND':
              this.goBackToSelectOrder();
              break;

            case 'ORDER_NOT_FOUND':
              events.emit('full-page-message', {
                title: 'Oops!',
                subtitle: this.session.route.ticketNumber
                  ? `Your order is no longer open for check number: ${this.session.route.ticketNumber}`
                  : 'Your order is no longer open.',
                description: 'Please ask the waitstaff for assistance.',
                button: 'goToDefaultPage'
              });
              break;

            default:
              logger.error(error);
              events.emit('full-page-message', {
                type: 'generic-p@t-error',
                button: 'goToDefaultPage'
              });
          }
        }
      },

      handleUserJoinedRoom() {
        const { enabled, triggerBeforeViewTicket, dollarTriggerAmount } = this.merchant.payAtTable2FA;

        const meetsCheckoutTotalThreshold = this.orderRoom.you.total >= dollarTriggerAmount;

        if (this.orderRoom.you.paid && this.orderRoom.status !== 'error') {
          /**
          * NOTE: If the user scans the QR code again to load the order
          * after they have paid, redirect them to their receipt
          */

          this.$router.replace({
            name: 'payAtTableReceipt',
            params: {
              merchantId: this.$route.params.merchantId,
              receiptToken: this.orderRoom.you.receiptToken
            }
          });
        }
        else if (enabled && triggerBeforeViewTicket && meetsCheckoutTotalThreshold && !this.session.isSignedIn) {
          this.isPending2FA = true;

          const passcodeModalPayload = {
            onSuccess: () => {
              this.isPending2FA = false;
            }
          };

          this.$openModal('requestTwoFactorAuthPasscodeModal', passcodeModalPayload, { isRequired: true });
        }
      },

      updateAllItemsSelected(allItemsSelected) {
        this.allItemsSelected = allItemsSelected;
      },

      updateYourCustomAmount() {
        this.$store.dispatch('socket/changeCustomPaymentAmount', {
          total: this.customAmount
        });
      },

      updateYourCustomAmountWithRemainingBalance() {
        const adjustedAmount = (Math.round(this.customAmount * 100) + Math.round(this.groupRemainingTotal * 100)) / 100;

        this.$store.dispatch('socket/changeCustomPaymentAmount', {
          total: adjustedAmount
        });
      },

      yourTotalChangeHandler(total) {
        this.customAmount = total || null;
      },

      handleSignatureComplete(signatureData) {
        this.signatureData = signatureData;
        this.signatureComplete = signatureData !== null;
      },

      async uploadSignature() {
        if (!this.signatureData) {
          return;
        }

        try {
          const response = await http.post('/cardfree-api/v1/assets', this.signatureData);
          return response.data;
        }
        catch (error) {
          errorHandlers.generic(error);
          throw error;
        }
      },

      orderAttachmentClick() {
        // this.$triggerGoogleTrackingEvent('ViewAttachment', 'Checkout');

        this.$triggerNewRelicEvent('ViewCheckoutAttachment', 'Information', 'Viewed Checkout Attachment');
      }
    }
  };
</script>
